import React, { useContext, useMemo } from "react";
import TravelCustomCssPreview from "./TravelCustomCssPreview";
import TravellerProfileForm from "src/pages/travel/tile-content/TravellerProfileForm";
import { useNavigate, useLocation } from "react-router-dom";
import { WorkspaceContext } from "src/context/workspace";

import ContentTypesRouter from "src/components/embedTypes/ContentTypesRouter";

const TravelEmbed = ({ content, onPageClick }) => {
  const { menus: workspaceMenus } = useContext(WorkspaceContext);
  const { travel_home_custom_html: custom_html } = content || {};
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const currentGroupParam = params.get("group") || params.get("travel-group");
  const pageParam = params.get("page");

  const filteredWorkspaceMenus = useMemo(() => {
    if (!Array.isArray(workspaceMenus) || workspaceMenus.length === 0) return [];
    if (!currentGroupParam) {
      return [workspaceMenus[0]];
    }
    const matched = workspaceMenus.filter((item) => item?.slug === currentGroupParam || item?._id === currentGroupParam);
    return matched.length > 0 ? matched : [workspaceMenus[0]];
  }, [workspaceMenus, currentGroupParam]);

  const flattenedWorkspaceMenus = useMemo(() => {
    if (!Array.isArray(filteredWorkspaceMenus)) return [];
    const flat = [];
    const extract = (items, currentGroupId) => {
      items.forEach((item) => {
        if (!item) return;
        const groupId = item?.slug || item?._id || item?.group_slug || item?.group_id || currentGroupId;
        const children = item?.submenu || item?.permissions || item?.pages;
        if (Array.isArray(children) && children.length > 0) {
          extract(children, groupId);
        } else {
          const merged = item?.page ? { ...item, ...item.page } : item;
          const pageSlug = merged?.slug || merged?.page_slug || merged?._id;
          const embedType = merged?.embed_type || (merged?.page_type === "TRAVEL" ? "TRAVEL_PROFILE" : merged?.page_type);

          flat.push({
            ...merged,
            slug: pageSlug,
            page_slug: pageSlug,
            name: merged?.name || item?.page_alias || item?.name,
            image: item?.image || merged?.image,
            groupId: merged?.groupId || groupId,
            embed_type: embedType,
            form_id: item?.form_id || merged?.form_id,
            document_id: item?.document_id || merged?.document_id,
          });
        }
      });
    };
    extract(filteredWorkspaceMenus);
    return flat;
  }, [filteredWorkspaceMenus]);

  const availablePages = Array.isArray(flattenedWorkspaceMenus) && flattenedWorkspaceMenus.length > 0 ? flattenedWorkspaceMenus : Array.isArray(filteredWorkspaceMenus) ? filteredWorkspaceMenus : [];

  const travelPagesRef = React.useRef([]);
  if (availablePages && availablePages.length > 0) {
    travelPagesRef.current = availablePages;
  }

  const activePageData = travelPagesRef.current.find((p) => (p?.slug || p?.page_slug || p?._id) === pageParam) || (pageParam ? null : travelPagesRef.current[0]);

  const isProfile = activePageData?.embed_type === "TRAVEL_PROFILE" || activePageData?.page_type === "TRAVEL" || content?.embed_type === "TRAVEL_PROFILE" || content?.data?.embed_type === "TRAVEL_PROFILE";

  const handlePageClick = (pageData) => {
    let slug = typeof pageData === "string" ? pageData : pageData?.slug;
    let itemLink = typeof pageData === "object" ? pageData?.itemLink : null;
    let groupId = typeof pageData === "object" ? pageData?.groupId : null;
    let contentType = typeof pageData === "object" ? pageData?.contentType : null;
    let pageType = typeof pageData === "object" ? pageData?.pageType : null;
    let pageLink = typeof pageData === "object" ? pageData?.pageLink : null;
    let pageLinkNewtab = typeof pageData === "object" ? pageData?.pageLinkNewtab : null;

    const matchedItem = travelPagesRef.current.find((p) => p?.slug === slug || p?._id === slug || p?.page_slug === slug || p?.page?.slug === slug || p?.page?._id === slug);

    const withFromTravel = (urlStr) => {
      if (!urlStr || typeof urlStr !== "string") return urlStr;
      try {
        const [path, searchStr] = urlStr.split("?");
        const p = new URLSearchParams(searchStr || "");
        p.set("from", "travel");
        return `${path}?${p.toString()}`;
      } catch {
        return urlStr;
      }
    };

    if (matchedItem?.link && matchedItem?.page_type !== "EXTERNAL_LINK" && matchedItem?.type !== "external") {
      navigate(withFromTravel(matchedItem.link));
      onPageClick?.(slug);
      return;
    }

    if (itemLink && typeof itemLink === "string" && itemLink.startsWith("/")) {
      navigate(withFromTravel(itemLink));
      onPageClick?.(slug);
      return;
    }

    if ((pageType === "EXTERNAL_LINK" || matchedItem?.page_type === "EXTERNAL_LINK") && (pageLink || matchedItem?.page_link)) {
      const link = pageLink || matchedItem?.page_link;
      const isNewTab = pageLinkNewtab === "true" || pageLinkNewtab === true || matchedItem?.page_link_newtab;
      window.open(link, isNewTab ? "_blank" : "_self");
      return;
    }

    const currentGroupId = groupId || matchedItem?.groupId || matchedItem?.group_id;
    const type = contentType || (matchedItem?.form_id ? "form" : matchedItem?.document_id ? "document" : "page");
    const paramKey = type === "form" ? "form" : type === "document" ? "document" : "page";

    const searchQueryParams = new URLSearchParams(location.search);
    if (currentGroupId) {
      searchQueryParams.set("group", currentGroupId);
    }
    searchQueryParams.set(paramKey, slug);
    searchQueryParams.set("from", "travel");
    navigate({ search: `?${searchQueryParams.toString()}` });
    onPageClick?.(slug);
  };

  if (isProfile && !!pageParam) {
    return (
      <TravellerProfileForm
        page={pageParam}
        group={currentGroupParam}
        onBack={() => {
          const searchQueryParams = new URLSearchParams(location.search);
          const defaultPage = travelPagesRef.current.find((p) => p.embed_type !== "TRAVEL_PROFILE");
          if (defaultPage) {
            searchQueryParams.set("page", defaultPage.slug || defaultPage._id);
          } else {
            params.delete("page");
            params.delete("from");
          }
          navigate({ search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
        }}
      />
    );
  }

  if (content?.config?.provider && content?.config?.type !== "TRAVEL" && !custom_html && !isProfile) {
    return (
      <div className="relative flex w-full flex-grow flex-col">
        <ContentTypesRouter
          content={content}
          setError={() => {}}
          setLoading={() => {}}
        />
      </div>
    );
  }

  if (!custom_html) {
    return <div className="rounded-md border border-dashed bg-gray-50 py-10 text-center text-sm text-gray-400">No HTML configured for this travel page.</div>;
  }

  return (
    <div className="relative h-full w-full">
      <TravelCustomCssPreview
        template={custom_html}
        pages={availablePages}
        onPageClick={handlePageClick}
        autoResize={true}
        bannerImage={content?.travel_home_banner_image || null}
        footerImage={content?.travel_home_footer_image || null}
      />
    </div>
  );
};

export default TravelEmbed;
